import { useTranslation } from "react-i18next";
import { AlarmClock, CalendarClock, User, UserCog } from "lucide-react";
import { localPick } from "../../lib/localName";
import { PRIORITY_TONE, STATUS_TONE } from "./taskMeta";
import { TaskCardActions } from "./TaskCardActions";

export const TaskCard = ({ task, userId, canManage, ...handlers }) => {
  const { t } = useTranslation();
  const tone = STATUS_TONE[task.status] || STATUS_TONE.new;
  const title = localPick(task, "title", "—");
  const desc = localPick(task, "description");
  const mine = userId && task.assignee_id === userId;
  return (
    <div className="relative overflow-hidden rounded-3xl bg-background p-5 shadow-neu-sm transition-shadow duration-300 hover:shadow-neu"
         data-testid={`task-card-${task.id}`}>
      <span className={`absolute inset-x-0 top-0 h-1.5 ${tone.bar}`} />
      <div className="flex items-start justify-between gap-2">
        <span className="num shrink-0 whitespace-nowrap font-mono text-base font-black text-blue-800 dark:text-blue-300 sm:text-xs">{task.code}</span>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <span className={`whitespace-nowrap text-sm font-black sm:text-[11px] ${PRIORITY_TONE[task.priority] || ""}`}>{t(`tasks.priority.${task.priority}`)}</span>
          <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-sm font-black sm:text-[11px] ${tone.badge}`}>{t(`tasks.status.${task.status}`)}</span>
        </div>
      </div>
      <h3 className="mt-2 text-lg font-black leading-snug sm:text-sm">{title}</h3>
      {desc ? <p className="mt-1 line-clamp-2 text-base text-muted-foreground sm:text-xs">{desc}</p> : null}
      <div className="mt-3">
        <div className="flex items-center justify-between text-sm font-bold text-muted-foreground sm:text-[11px]">
          <span>{t("tasks.labels.progress")}</span><span className="num">{task.progress}%</span>
        </div>
        <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted shadow-neu-in">
          <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${task.progress}%` }} />
        </div>
      </div>
      <div className="mt-3 grid gap-1 text-sm text-muted-foreground sm:text-[11px]">
        <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {t("tasks.labels.assignee")}: <b className="text-foreground">{task.assignee_name || "—"}</b></span>
        <span className="flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" /> {t("tasks.labels.creator")}: <b className="text-foreground">{task.created_by_name || "—"}</b></span>
        <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t("tasks.labels.due")}: <b className="num text-foreground" dir="ltr">{task.due_date}{task.due_time ? ` ${task.due_time}` : ""}</b></span>
        {task.is_overdue ? <span className="flex items-center gap-1.5 font-black text-rose-600"><AlarmClock className="h-3.5 w-3.5" /> {t("tasks.badge.overdueDays", { count: task.days_overdue })}</span> : null}
      </div>
      <TaskCardActions task={task} mine={mine} canManage={canManage} {...handlers} />
    </div>
  );
};
